import React, { useEffect, useState, useId } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Car, Store, Shield, ArrowRight, Layers, Loader2, UtensilsCrossed, ImageIcon, CheckCircle2, XCircle, MessageCircle, Eye, EyeOff, Clock, AlertCircle, Inbox, Check, X, FileText, BarChart3, Receipt, LogOut, UserIcon, ChefHat, ShieldCheck, Ban, PlayCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Modal } from '../../components/ui/Modal';
import { AnimatePresence, motion } from 'framer-motion';
import { useIdleLogout } from '../../lib/useIdleLogout';
interface RestaurantApplication {
  id: string;
  owner_name: string;
  restaurant_name: string;
  whatsapp: string;
  social_handle: string | null;
  business_type: string;
  uses_whatsapp_orders: boolean;
  has_digital_menu: boolean;
  main_goal: string | null;
  status: 'pending' | 'contacted' | 'approved' | 'rejected';
  created_at: string;
}
const ENROLLMENT_OPTIONS = [{
  title: 'Conductor',
  description: 'Registrar conductor, crear o reutilizar vehículo y dejar accesos listos',
  route: '/admin/drivers/new',
  icon: Car,
  accent: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
  iconBg: 'bg-blue-500/15',
  btnClass: 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
}, {
  title: 'Subir restaurante',
  description: 'Registrar restaurante, crear acceso inicial y dejarlo listo para operar',
  route: '/admin/enrollment/restaurant',
  icon: Store,
  accent: 'bg-purple-500/10 border-purple-500/20 text-purple-400',
  iconBg: 'bg-purple-500/15',
  btnClass: 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
}, {
  title: 'Generar Recibo',
  description: 'Crear recibos de pago profesionales para clientes, imprimir o compartir por WhatsApp',
  route: '/admin/enrollment/receipts',
  icon: Receipt,
  accent: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
  iconBg: 'bg-emerald-500/15',
  btnClass: 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
}, {
  title: 'Admin de NOIZA',
  description: 'Crear usuario interno de NOIZA y asignar permisos de plataforma',
  route: '/admin/enrollment/platform-admin',
  icon: Shield,
  accent: 'bg-[#006847]/10 border-[#006847]/20 text-[#00a870]',
  iconBg: 'bg-[#006847]/20',
  btnClass: 'bg-[#006847]/20 text-[#00a870] hover:bg-[#006847]/30'
}, {
  title: 'Admin de Restaurante',
  description: 'Asignar usuario existente como administrador de KDS para un restaurante.',
  route: '/admin/enrollment/restaurant-admin',
  icon: ShieldCheck,
  accent: 'bg-indigo-500/10 border-indigo-500/20 text-indigo-400',
  iconBg: 'bg-indigo-500/15',
  btnClass: 'bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30',
  btnLabel: 'Asignar admin'
}, {
  title: 'Actualizar menú',
  description: 'Agregar categorías, productos u opciones a un restaurante existente sin recrearlo',
  route: '/admin/enrollment/menu-update',
  icon: UtensilsCrossed,
  accent: 'bg-amber-500/10 border-amber-500/20 text-amber-400',
  iconBg: 'bg-amber-500/15',
  btnClass: 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30',
  btnLabel: 'Iniciar actualización'
}, {
  title: 'Subir imágenes del menú',
  description: 'Relaciona imágenes con productos del menú usando el nombre del archivo',
  route: '/admin/enrollment/menu-images',
  icon: ImageIcon,
  accent: 'bg-teal-500/10 border-teal-500/20 text-teal-400',
  iconBg: 'bg-teal-500/15',
  btnClass: 'bg-teal-500/20 text-teal-400 hover:bg-teal-500/30',
  btnLabel: 'Abrir módulo'
}, {
  title: 'Métricas NOIZA',
  description: 'Revisa métricas globales, crecimiento y operación de NOIZA',
  route: '/admin/enrollment/metrics',
  icon: BarChart3,
  accent: 'bg-rose-500/10 border-rose-500/20 text-rose-400',
  iconBg: 'bg-rose-500/15',
  btnClass: 'bg-rose-500/20 text-rose-400 hover:bg-rose-500/30',
  btnLabel: 'Ver métricas'
}];
export function EnrollmentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // --- Access Guard State ---
  const [guardState, setGuardState] = useState<'loading' | 'authorized' | 'redirecting'>('loading');
  // --- Idle Logout ---
  const {
    showWarning: showIdleWarning
  } = useIdleLogout({
    enabled: guardState === 'authorized',
    timeoutMs: 900000,
    warningBeforeMs: 60000,
    redirectTo: '/admin/login'
  });
  // --- Active Restaurants State ---
  const [restaurants, setRestaurants] = useState<{
    id: string;
    name: string;
    slug: string;
    logo_url: string | null;
    show_in_marketplace: boolean;
    access_status: string | null;
  }[]>([]);
  const [restLoading, setRestLoading] = useState(true);
  const [togglingVisibilityId, setTogglingVisibilityId] = useState<string | null>(null);
  // --- Suspensión administrativa ---
  const [suspensionTarget, setSuspensionTarget] = useState<{
    id: string;
    name: string;
    suspend: boolean;
  } | null>(null);
  const [suspensionSaving, setSuspensionSaving] = useState(false);
  // --- Applications State ---
  const [applications, setApplications] = useState<RestaurantApplication[]>([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [appsError, setAppsError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'contacted' | 'approved' | 'rejected'>('all');
  const [selectedApp, setSelectedApp] = useState<RestaurantApplication | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [appsModalOpen, setAppsModalOpen] = useState(false);
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  const [sessionUserId, setSessionUserId] = useState<string | null>(null);
  const [showSessionInfo, setShowSessionInfo] = useState(false);
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login', {
      replace: true
    });
  };
  const fetchApplications = async () => {
    setAppsLoading(true);
    setAppsError(null);
    try {
      const {
        data,
        error
      } = await supabase.from('restaurant_applications').select('*').order('created_at', {
        ascending: false
      });
      if (error) throw error;
      setApplications(data || []);
    } catch (err: any) {
      console.error('Error fetching applications:', err);
      setAppsError(err.message);
    } finally {
      setAppsLoading(false);
    }
  };
  // ─── Access Guard ─────────────────────────────────────────────
  useEffect(() => {
    async function guard() {
      try {
        // 1. Check authenticated user
        const {
          data: {
            user
          },
          error: userError
        } = await supabase.auth.getUser();
        if (userError || !user) {
          console.log('[EnrollmentPage] No authenticated user — redirecting to /admin/login');
          setGuardState('redirecting');
          navigate('/admin/login', {
            state: {
              from: location.pathname
            },
            replace: true
          });
          return;
        }
        // 2. Check platform_admins table first (primary source)
        let accessRole: string | null = null;
        const {
          data: platformData,
          error: platformError
        } = await supabase.from('platform_admins').select('role').eq('user_id', user.id).eq('is_active', true).maybeSingle();
        if (!platformError && platformData && (platformData.role === 'super_admin' || platformData.role === 'platform_admin')) {
          accessRole = platformData.role;
        }
        // 3. Fallback: check restaurant_users table
        if (!accessRole) {
          const {
            data: roleData,
            error: roleError
          } = await supabase.from('restaurant_users').select('role').eq('user_id', user.id).eq('is_active', true).in('role', ['super_admin', 'platform_admin']).limit(1).maybeSingle();
          if (roleError) {
            console.error('[EnrollmentPage] Role check error:', roleError);
          }
          if (roleData) {
            accessRole = roleData.role;
          }
        }
        if (!accessRole) {
          console.log('[EnrollmentPage] User lacks required role — redirecting to /');
          setGuardState('redirecting');
          navigate('/', {
            replace: true
          });
          return;
        }
        console.log('[EnrollmentPage] Access granted:', {
          email: user.email,
          role: accessRole
        });
        setSessionEmail(user.email || null);
        setSessionUserId(user.id);
        setGuardState('authorized');
      } catch (err) {
        console.error('[EnrollmentPage] Guard error:', err);
        setGuardState('redirecting');
        navigate('/admin/login', {
          replace: true
        });
      }
    }
    guard();
  }, [navigate, location.pathname]);
  const fetchRestaurants = async () => {
    setRestLoading(true);
    try {
      const {
        data,
        error
      } = await supabase.from('restaurants').select('id, name, slug, logo_url, show_in_marketplace, access_status').eq('is_active', true).order('name', {
        ascending: true
      });
      if (error) throw error;
      setRestaurants(data || []);
    } catch (err: any) {
      console.error('Error fetching restaurants:', err);
    } finally {
      setRestLoading(false);
    }
  };
  const toggleMarketplaceVisibility = async (restaurantId: string, currentValue: boolean) => {
    setTogglingVisibilityId(restaurantId);
    const newValue = !currentValue;
    // Optimistic update
    setRestaurants(prev => prev.map(r => r.id === restaurantId ? {
      ...r,
      show_in_marketplace: newValue
    } : r));
    try {
      const {
        data,
        error
      } = await supabase.from('restaurants').update({
        show_in_marketplace: newValue
      }).eq('id', restaurantId).select('id, show_in_marketplace');
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error('La actualización fue bloqueada por RLS. Corré FIX_RLS_RESTAURANTS_MARKETPLACE_TOGGLE.sql en Supabase.');
      }
      setToast({
        message: newValue ? 'Restaurante visible en el marketplace' : 'Restaurante oculto del marketplace',
        type: 'success'
      });
    } catch (err: any) {
      console.error('Error toggling marketplace visibility:', err);
      // Revert on error
      setRestaurants(prev => prev.map(r => r.id === restaurantId ? {
        ...r,
        show_in_marketplace: currentValue
      } : r));
      setToast({
        message: 'No se pudo actualizar la visibilidad',
        type: 'error'
      });
    } finally {
      setTogglingVisibilityId(null);
      setTimeout(() => setToast(null), 3000);
    }
  };
  /**
   * Suspende o reactiva un restaurante.
   *
   * La escritura NUNCA la hace el navegador contra `restaurants`: se llama
   * a `admin_set_restaurant_suspension_v1`, que valida en el servidor que
   * `auth.uid()` sea Super Admin de plataforma y solo puede escribir las 5
   * columnas de suspensión. Si quien llama no es Super Admin, la base
   * rechaza la operación aunque el botón exista en pantalla.
   */
  const applySuspension = async () => {
    if (!suspensionTarget) return;
    const {
      id,
      name,
      suspend
    } = suspensionTarget;
    setSuspensionSaving(true);
    try {
      const {
        data,
        error
      } = await supabase.rpc('admin_set_restaurant_suspension_v1', {
        p_restaurant_id: Number(id),
        p_suspend: suspend
      });
      if (error) throw error;
      const row = (Array.isArray(data) ? data[0] ?? null : data) as {
        access_status: string | null;
      } | null;
      setRestaurants(prev => prev.map(r => r.id === id ? {
        ...r,
        access_status: row?.access_status ?? (suspend ? 'suspended_payment' : 'active')
      } : r));
      setToast({
        message: suspend ? `${name} quedó suspendido` : `${name} volvió a estar activo`,
        type: 'success'
      });
      setSuspensionTarget(null);
    } catch (err: any) {
      console.error('Error updating suspension:', err);
      setToast({
        message: err?.code === '42501' ? 'Solo un Super Admin de plataforma puede hacer este cambio' : err?.message?.includes('admin_set_restaurant_suspension_v1') ? 'Falta instalar SETUP_RESTAURANT_SUSPENSION.sql en Supabase' : 'No se pudo actualizar el estado del restaurante',
        type: 'error'
      });
    } finally {
      setSuspensionSaving(false);
      setTimeout(() => setToast(null), 4000);
    }
  };
  useEffect(() => {
    if (guardState === 'authorized') {
      fetchApplications();
      fetchRestaurants();
    }
  }, [guardState]);
  const updateStatus = async (id: string, newStatus: 'pending' | 'contacted' | 'approved' | 'rejected') => {
    setUpdatingId(id);
    try {
      const {
        error
      } = await supabase.from('restaurant_applications').update({
        status: newStatus
      }).eq('id', id);
      if (error) throw error;
      setToast({
        message: 'Estado actualizado correctamente',
        type: 'success'
      });
      await fetchApplications();
      if (selectedApp && selectedApp.id === id) {
        setSelectedApp({
          ...selectedApp,
          status: newStatus
        });
      }
    } catch (err: any) {
      console.error('Error updating status:', err);
      setToast({
        message: 'Error al actualizar estado',
        type: 'error'
      });
    } finally {
      setUpdatingId(null);
      setTimeout(() => setToast(null), 3000);
    }
  };
  const handleWhatsApp = (whatsapp: string) => {
    const cleanNumber = whatsapp.replace(/\D/g, '');
    if (cleanNumber) {
      window.open(`https://wa.me/${cleanNumber}`, '_blank');
    }
  };
  const filteredApps = applications.filter(app => statusFilter === 'all' || app.status === statusFilter);
  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    contacted: applications.filter(a => a.status === 'contacted').length,
    approved: applications.filter(a => a.status === 'approved').length,
    rejected: applications.filter(a => a.status === 'rejected').length
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/10 text-amber-500 border border-amber-500/20" data-id="element-4299" data-mp="1dz14w3cew5cd">
            Pendiente
          </span>;
      case 'contacted':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-blue-500/10 text-blue-500 border border-blue-500/20" data-id="element-4300" data-mp="26n5c4tamsqh6">
            Contactado
          </span>;
      case 'approved':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500/10 text-emerald-500 border border-emerald-500/20" data-id="element-4301" data-mp="1xlyps3lawlg0">
            Aprobado
          </span>;
      case 'rejected':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-red-500/10 text-red-500 border border-red-500/20" data-id="element-4302" data-mp="m6a6pnb646kh">
            Rechazado
          </span>;
      default:
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-gray-500/10 text-gray-400 border border-gray-500/20" data-id="element-4303" data-mp="2j0s9i3vgzu0v">
            {status}
          </span>;
    }
  };
  if (guardState !== 'authorized') {
    return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-4304" data-mp="257kiadbax0jp">
        <Loader2 className="w-8 h-8 text-[#006847] animate-spin" data-id="element-4305" data-mp="2087tvo4s7lf0" />
      </div>;
  }
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans" data-id="element-4306" data-mp="1v8v52xuco49s">
      {/* Idle Warning Banner */}
      <AnimatePresence data-id="element-4307" data-mp="1r7jh0wr4jr3p">
        {showIdleWarning && <motion.div initial={{
        y: -60,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: -60,
        opacity: 0
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed top-0 left-0 right-0 z-[70] flex items-center justify-center px-4 py-3 bg-amber-500/90 text-[#0B0B0B]" data-id="element-4308" data-mp="1r92xancs625h">
            <Clock className="w-4 h-4 mr-2 shrink-0" data-id="element-4309" data-mp="1l5g5cncqtzuk" />
            <span className="text-sm font-bold" data-id="element-4310" data-mp="1l450ek7kam1v">
              Por seguridad, tu sesión se cerrará pronto por inactividad.
            </span>
          </motion.div>}
      </AnimatePresence>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 space-y-10" data-id="element-4311" data-mp="te0koyuqyn6z">
        {/* Header */}
        <div className="flex items-center justify-between gap-4" data-id="element-4312" data-mp="xe0ycha0uucz">
          <div className="flex items-center gap-4" data-id="element-4313" data-mp="12fhyubolget1">
            <div className="w-14 h-14 bg-[#141414] border border-white/10 rounded-2xl flex items-center justify-center" data-id="element-4314" data-mp="12c2iz6n3j1qk">
              <Layers className="w-7 h-7 text-[#006847]" data-id="element-4315" data-mp="2dtc0voqktlpr" />
            </div>
            <div data-id="element-4316" data-mp="2dvf1f2pcux8q">
              <h1 className="text-3xl font-black tracking-tight" data-id="element-4317" data-mp="1pgs6bjs6wavi">
                NOIZA Enrollment
              </h1>
              <p className="text-white/40 text-sm mt-1" data-id="element-4318" data-mp="1kiqdi74yox6u">
                Gestiona los diferentes tipos de registro y acceso dentro de
                NOIZA
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0" data-id="element-4319" data-mp="1thkiaksg4tvh">
            {/* Session Info Button */}
            <div className="relative" data-id="element-4320" data-mp="286gosdeutytz">
              <button onClick={() => setShowSessionInfo(!showSessionInfo)} className="flex items-center gap-2 px-3 py-2.5 bg-[#141414] border border-white/10 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:border-white/20 transition-colors" data-id="element-4321" data-mp="289mb5kw3mmm0">
                <UserIcon className="w-4 h-4" data-id="element-4322" data-mp="2d4rzeqcz4dd9" />
                <span className="hidden sm:inline max-w-[160px] truncate" data-id="element-4323" data-mp="mv83yu3h7r1s">
                  {sessionEmail || 'Usuario'}
                </span>
              </button>

              {/* Session Info Dropdown */}
              <AnimatePresence data-id="element-4324" data-mp="8py25cw4757q">
                {showSessionInfo && <motion.div initial={{
                opacity: 0,
                y: -4,
                scale: 0.95
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -4,
                scale: 0.95
              }} transition={{
                duration: 0.15
              }} className="absolute right-0 top-full mt-2 w-72 bg-[#141414] border border-white/10 rounded-2xl p-4 shadow-2xl z-50" data-id="element-4325" data-mp="cqi02ioiicux">
                    <div className="space-y-3" data-id="element-4326" data-mp="1aejr5bbqk5dh">
                      <div data-id="element-4327" data-mp="1fctn3o5aba05">
                        <p className="text-[10px] uppercase tracking-wider text-white/30 font-bold mb-1" data-id="element-4328" data-mp="1fc20h2to0mm0">
                          Sesión activa
                        </p>
                        <p className="text-sm font-bold text-white break-all" data-id="element-4329" data-mp="2nnq23o364wzs">
                          {sessionEmail || '—'}
                        </p>
                      </div>
                      {sessionUserId && <div data-id="element-4330" data-mp="2dz81p7yswrwo">
                          <p className="text-[10px] uppercase tracking-wider text-white/30 font-bold mb-1" data-id="element-4331" data-mp="2dyg6hy948w6x">
                            User ID
                          </p>
                          <p className="text-[11px] font-mono text-white/50 break-all" data-id="element-4332" data-mp="36lj3gly58yv2">
                            {sessionUserId}
                          </p>
                        </div>}
                      <div className="pt-2 border-t border-white/5" data-id="element-4333" data-mp="2wn3asejgxhjg">
                        <button onClick={handleSignOut} className="w-full flex items-center justify-center gap-2 py-2.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-xl text-sm font-bold transition-colors" data-id="element-4334" data-mp="1h7ny7msfc8fp">
                          <LogOut className="w-4 h-4" data-id="element-4335" data-mp="18roqazqie5gi" />
                          Cerrar sesión
                        </button>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Logout Button */}
            <button onClick={handleSignOut} className="p-2.5 bg-[#141414] border border-white/10 rounded-xl text-white/50 hover:text-red-400 hover:border-red-500/20 transition-colors" title="Cerrar sesión" data-id="element-4336" data-mp="v0jwie9to7e3">
              <LogOut className="w-5 h-5" data-id="element-4337" data-mp="zyld8sjrzume" />
            </button>
          </div>
        </div>

        {/* Question */}
        <p className="text-white/50 text-lg font-medium" data-id="element-4338" data-mp="c8haeneevywg">
          ¿Qué deseas registrar hoy?
        </p>

        {/* Cards */}
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-4339" data-mp="794oc4vd6vux">
          {ENROLLMENT_OPTIONS.map(opt => {
          const Icon = opt.icon;
          return <div key={opt.route} className="bg-[#141414] border border-white/10 rounded-2xl p-6 flex flex-col justify-between gap-6 hover:border-white/20 transition-colors" data-id="element-4340" data-mp="10yvfkkeawkhb">
                <div className="space-y-4" data-id="element-4341" data-mp="1ao59m5spgd82">
                  <div className={`w-12 h-12 ${opt.iconBg} rounded-xl flex items-center justify-center`} data-id="element-4342" data-mp="dz7cigxhg37q">
                    <Icon className="w-6 h-6" data-id="element-4343" data-mp="i1uaz0onsmdw" />
                  </div>
                  <div data-id="element-4344" data-mp="mzckas6l93mf">
                    <h2 className="text-lg font-bold text-white" data-id="element-4345" data-mp="myucqzeh72z7">
                      {opt.title}
                    </h2>
                    <p className="text-sm text-white/40 mt-1 leading-relaxed" data-id="element-4346" data-mp="s0a04y44llkr">
                      {opt.description}
                    </p>
                  </div>
                </div>

                <button onClick={() => navigate(opt.route)} className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors ${opt.btnClass}`} data-id="element-4347" data-mp="23ckjkdwzc5cj">
                  {(opt as any).btnLabel || 'Iniciar enrollment'}
                  <ArrowRight className="w-4 h-4" data-id="element-4348" data-mp="3e4jzkm6m7q8e" />
                </button>
              </div>;
        })}
        </div>

        {/* ═══ COMPACT APPLICATIONS CARD ═══ */}
        <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 flex flex-col justify-between gap-6 hover:border-white/20 transition-colors" data-id="element-4349" data-mp="356gc3kc2kjdy">
          <div className="space-y-4" data-id="element-4350" data-mp="2v78zizd5ssrw">
            <div className="flex items-start justify-between" data-id="element-4351" data-mp="2v8abd6201jao">
              <div className="w-12 h-12 bg-orange-500/15 rounded-xl flex items-center justify-center" data-id="element-4352" data-mp="2q8nzazxwq93g">
                <FileText className="w-6 h-6 text-orange-400" data-id="element-4353" data-mp="tgnuhxbtn4rx" />
              </div>
              {stats.pending > 0 && <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/10 text-amber-500 border border-amber-500/20 animate-pulse" data-id="element-4354" data-mp="oi46zshkxkxg">
                  {stats.pending} pendiente{stats.pending !== 1 ? 's' : ''}
                </span>}
            </div>
            <div data-id="element-4355" data-mp="mk2u881irgfy">
              <h2 className="text-lg font-bold text-white" data-id="element-4356" data-mp="mijd6f81h1d4">
                Aplicaciones de restaurantes
              </h2>
              <p className="text-sm text-white/40 mt-1 leading-relaxed" data-id="element-4357" data-mp="rgt76mi11vx5">
                Revisa las solicitudes de ingreso a NOIZA
              </p>
            </div>

            {/* Compact Stats Row */}
            <div className="flex flex-wrap gap-3 pt-2" data-id="element-4358" data-mp="ijakldoqj9j5">
              <div className="flex items-center gap-1.5" data-id="element-4359" data-mp="3tuulzx1384i">
                <div className="w-2 h-2 rounded-full bg-white/30" data-id="element-4360" data-mp="3r8m9y2bcol8" />
                <span className="text-xs text-white/50" data-id="element-4361" data-mp="8so5dkkxkl0z">
                  <span className="font-bold text-white" data-id="element-4362" data-mp="3qx9dg6geuesf">{stats.total}</span>{' '}
                  total
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-4363" data-mp="3uyl48fb0t627">
                <div className="w-2 h-2 rounded-full bg-amber-500" data-id="element-4364" data-mp="3caankfx7bsft" />
                <span className="text-xs text-white/50" data-id="element-4365" data-mp="37bzdq56wphxl">
                  <span className="font-bold text-amber-500" data-id="element-4366" data-mp="37dk6pqdkostc">
                    {stats.pending}
                  </span>{' '}
                  pendientes
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-4367" data-mp="3gb4ap72tl37n">
                <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-4368" data-mp="3v9et3epjb42q" />
                <span className="text-xs text-white/50" data-id="element-4369" data-mp="3v7lkvuz9wbsm">
                  <span className="font-bold text-blue-500" data-id="element-4370" data-mp="3q9sdiym8hbqr">
                    {stats.contacted}
                  </span>{' '}
                  contactados
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-4371" data-mp="3wk5n9i39if3">
                <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-4372" data-mp="husa02hgfm0a" />
                <span className="text-xs text-white/50" data-id="element-4373" data-mp="hsxoalyczpv5">
                  <span className="font-bold text-emerald-500" data-id="element-4374" data-mp="cx076df9ufhb">
                    {stats.approved}
                  </span>{' '}
                  aprobados
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-4375" data-mp="3aqk2r33eb4n1">
                <div className="w-2 h-2 rounded-full bg-red-500" data-id="element-4376" data-mp="3foljhhdcmrvc" />
                <span className="text-xs text-white/50" data-id="element-4377" data-mp="3fnagvkvn3q5t">
                  <span className="font-bold text-red-500" data-id="element-4378" data-mp="3kfv4g4nyd70w">
                    {stats.rejected}
                  </span>{' '}
                  rechazados
                </span>
              </div>
            </div>
          </div>

          <button onClick={() => setAppsModalOpen(true)} className="w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors bg-orange-500/20 text-orange-400 hover:bg-orange-500/30" data-id="element-4379" data-mp="3uby8jg2d7fqh">
            Ver aplicaciones
            <ArrowRight className="w-4 h-4" data-id="element-4380" data-mp="242dfyxkcc265" />
          </button>
        </div>

        {/* ═══ ACCESO RÁPIDO KDS ═══ */}
        <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-colors" data-id="element-4381" data-mp="290eynhdutzh3">
          <div className="space-y-4" data-id="element-4382" data-mp="21igs9lqyz4i3">
            <div className="flex items-start justify-between" data-id="element-4383" data-mp="1wpnmqvnofs4y">
              <div className="flex items-center gap-3" data-id="element-4384" data-mp="1wo45b1qabb1l">
                <div className="w-12 h-12 bg-cyan-500/15 rounded-xl flex items-center justify-center" data-id="element-4385" data-mp="1rrm6eihmy6x6">
                  <ChefHat className="w-6 h-6 text-cyan-400" data-id="element-4386" data-mp="1mt2k2gz4qt4a" />
                </div>
                <div data-id="element-4387" data-mp="2kegoj8qzcu7h">
                  <h2 className="text-lg font-bold text-white" data-id="element-4388" data-mp="2kezwpxqtw48b">
                    Acceso rápido KDS
                  </h2>
                  <p className="text-sm text-white/40 mt-0.5" data-id="element-4389" data-mp="2felxn577g4mf">
                    Ir directo a la cocina de cualquier restaurante
                  </p>
                </div>
              </div>
              <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-cyan-500/10 text-cyan-400 border border-cyan-500/20" data-id="element-4390" data-mp="26htgoaxhojms">
                {restaurants.length} activos
              </span>
            </div>

            {restLoading ? <div className="flex items-center justify-center py-6" data-id="element-4391" data-mp="2u5232t9n3vjy">
                <Loader2 className="w-6 h-6 text-white/20 animate-spin" data-id="element-4392" data-mp="2z4er3hc8z8o4" />
              </div> : restaurants.length === 0 ? <p className="text-white/30 text-sm text-center py-4" data-id="element-4393" data-mp="2l5ostydoz2y6">
                No hay restaurantes activos
              </p> : <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 pt-2" data-id="element-4394" data-mp="2q5kw1vekeqco">
                {restaurants.map(r => {
              const isHidden = r.show_in_marketplace === false;
              const isToggling = togglingVisibilityId === r.id;
              // Único valor que habilita la operación. Cualquier otro
              // suspende, sin lista cerrada de motivos.
              const isSuspended = (r.access_status || 'active').trim().toLowerCase() !== 'active';
              return <div key={r.id} className="relative flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-xl hover:border-cyan-500/30 hover:bg-cyan-500/5 transition-colors group" data-id="element-4395" data-mp="5f29of72wb0s">
                      <button onClick={() =>
                // Emilius Pizza no usa el KDS como pantalla de
                // entrada: su operación arranca en Pedidos Web,
                // igual que al iniciar sesión. El resto de
                // restaurantes sigue entrando al KDS.
                navigate(r.slug === 'emilius-pizza' ? `/admin/${r.slug}/pedidos-web` : `/admin/${r.slug}/kds`)} className="flex items-center gap-3 flex-1 min-w-0 text-left" data-id="element-4396" data-mp="oinpmjbrptiv">
                        <div className="w-10 h-10 rounded-lg bg-white/10 overflow-hidden shrink-0 flex items-center justify-center" data-id="element-4397" data-mp="1c6x4106njezz">
                          {r.logo_url ? <img src={r.logo_url} alt={r.name} className="w-full h-full object-cover" data-id="element-4398" data-mp="2w90ywg3xck3i" /> : <Store className="w-5 h-5 text-white/30" data-id="element-4399" data-mp="2naystm5c319d" />}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-4400" data-mp="3fxjgabiout7n">
                          <div className="font-bold text-white text-sm truncate group-hover:text-cyan-300 transition-colors flex items-center gap-1.5" data-id="element-4401" data-mp="3fy2zrwnzlpns">
                            {r.name}
                            {isSuspended ? <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-500/15 text-red-400 border border-red-500/20 shrink-0" data-id="element-4402" data-mp="7h7z8nunwhcv">
                                SUSPENDIDO
                              </span> : null}
                            {isHidden && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/15 text-amber-400 border border-amber-500/20 shrink-0" data-id="element-4403" data-mp="f07fwlqqnusj">
                                OCULTO
                              </span>}
                          </div>
                          <div className="text-xs text-white/30 truncate" data-id="element-4404" data-mp="t1a16w9wt12e">
                            {r.slug}
                          </div>
                        </div>
                      </button>
                      <button onClick={e => {
                  e.stopPropagation();
                  toggleMarketplaceVisibility(r.id, r.show_in_marketplace !== false);
                }} disabled={isToggling} title={isHidden ? 'Mostrar en el marketplace' : 'Ocultar del marketplace'} aria-label={isHidden ? 'Mostrar en el marketplace' : 'Ocultar del marketplace'} className={`p-2 rounded-lg border transition-colors shrink-0 ${isHidden ? 'bg-amber-500/10 border-amber-500/30 text-amber-400 hover:bg-amber-500/20' : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10 hover:text-white/80'} ${isToggling ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-4405" data-mp="12qbhyooa3mhf">
                        {isToggling ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-4406" data-mp="2b45gf90a5cnv" /> : isHidden ? <EyeOff className="w-4 h-4" data-id="element-4407" data-mp="265ampc0ry3oz" /> : <Eye className="w-4 h-4" data-id="element-4408" data-mp="267ey0f1064za" />}
                      </button>
                      <button onClick={e => {
                  e.stopPropagation();
                  setSuspensionTarget({
                    id: r.id,
                    name: r.name,
                    suspend: !isSuspended
                  });
                }} title={isSuspended ? 'Reactivar restaurante' : 'Suspender restaurante'} aria-label={isSuspended ? `Reactivar ${r.name}` : `Suspender ${r.name}`} className={`p-2 rounded-lg border transition-colors shrink-0 ${isSuspended ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20' : 'bg-white/5 border-white/10 text-white/50 hover:bg-red-500/10 hover:border-red-500/30 hover:text-red-400'}`} data-id="element-4409" data-mp="qm38l3v9ejv9">
                        {isSuspended ? <PlayCircle className="w-4 h-4" data-id="element-4410" data-mp="tltol7qyepco" /> : <Ban className="w-4 h-4" data-id="element-4411" data-mp="oo1ovrk2hyxx" />}
                      </button>
                    </div>;
            })}
              </div>}
          </div>
        </div>

        {/* ═══ DOCUMENTOS LEGALES ═══ */}
        <div className="pt-4 border-t border-white/10" data-id="element-4412" data-mp="ftdl65u54pb8">
          <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-3" data-id="element-4413" data-mp="awww99f4a6x3">
            Documentos legales
          </p>
          <div className="flex flex-wrap gap-3" data-id="element-4414" data-mp="5v7hbiorx93q">
            <a href="/terminos-restaurantes" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#141414] border border-white/10 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:border-white/20 transition-colors" data-id="element-4415" data-mp="ww736ukgwkz">
              <FileText className="w-4 h-4 text-[#006847]" data-id="element-4416" data-mp="2cn5hy6qyf9cq" />
              T&C Restaurantes
              <ArrowRight className="w-3.5 h-3.5 text-white/30" data-id="element-4417" data-mp="2hiayeq1l7vtf" />
            </a>
            <a href="/terminos-comensales" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#141414] border border-white/10 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:border-white/20 transition-colors" data-id="element-4418" data-mp="2miys5jh7aqrc">
              <FileText className="w-4 h-4 text-blue-400" data-id="element-4419" data-mp="23kcr24looznf" />
              T&C Comensales
              <ArrowRight className="w-3.5 h-3.5 text-white/30" data-id="element-4420" data-mp="1658naai9bygn" />
            </a>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ═══ FULL-SCREEN APPLICATIONS MANAGER MODAL ═══ */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <AnimatePresence data-id="element-4421" data-mp="z0z84x73glkg">
        {appsModalOpen && <motion.div className="fixed inset-0 z-50 flex items-stretch justify-end" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} data-id="element-4422" data-mp="1ctfhefql9ftt">
            {/* Backdrop */}
            <motion.div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setAppsModalOpen(false)} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-4423" data-mp="u2xs6l4yt2d7" />

            {/* Slide-over Panel */}
            <motion.div className="relative w-full sm:w-[640px] lg:w-[780px] h-full bg-[#0B0B0B] border-l border-white/10 shadow-2xl flex flex-col overflow-hidden" initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} data-id="element-4424" data-mp="3igwfifv6wo2d">
              {/* Panel Header */}
              <div className="shrink-0 px-6 py-5 border-b border-white/10 bg-[#0B0B0B]" data-id="element-4425" data-mp="a01p3zvkww56">
                <div className="flex items-center justify-between" data-id="element-4426" data-mp="3seasg5hwmupx">
                  <div className="flex items-center gap-3" data-id="element-4427" data-mp="17q08ig45grb">
                    <div className="w-10 h-10 bg-orange-500/15 rounded-xl flex items-center justify-center" data-id="element-4428" data-mp="oy3rw7usddld">
                      <FileText className="w-5 h-5 text-orange-400" data-id="element-4429" data-mp="tok5brylwz7q" />
                    </div>
                    <div data-id="element-4430" data-mp="2pryph2y5c0r5">
                      <h2 className="text-xl font-bold text-white" data-id="element-4431" data-mp="2ps8bkfg2lnrk">
                        Aplicaciones de restaurantes
                      </h2>
                      <p className="text-white/40 text-xs mt-0.5" data-id="element-4432" data-mp="2kz5jkbqxymdh">
                        Centro de control de solicitudes
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setAppsModalOpen(false)} className="p-2 -mr-2 rounded-xl hover:bg-white/10 text-white/60 hover:text-white transition-colors" data-id="element-4433" data-mp="2b0zifwcylk9o">
                    <X className="w-6 h-6" data-id="element-4434" data-mp="2ysd8twrjv5s0" />
                  </button>
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-5 gap-3 mt-5" data-id="element-4435" data-mp="2uq04ncsgbfxc">
                  <div className="bg-[#141414] border border-white/10 rounded-xl p-3 text-center" data-id="element-4436" data-mp="1ndhygmzxrqh9">
                    <span className="text-white/40 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-4437" data-mp="1jbwe6gl2o9x1">
                      Total
                    </span>
                    <span className="text-xl font-black text-white" data-id="element-4438" data-mp="3v12tgv96zw2j">
                      {stats.total}
                    </span>
                  </div>
                  <div className="bg-[#141414] border border-amber-500/20 rounded-xl p-3 text-center" data-id="element-4439" data-mp="7ua8nhl26i7p">
                    <span className="text-amber-500/60 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-4440" data-mp="clihabekdzjt">
                      Pendientes
                    </span>
                    <span className="text-xl font-black text-amber-500" data-id="element-4441" data-mp="mj6dl5rtjf43">
                      {stats.pending}
                    </span>
                  </div>
                  <div className="bg-[#141414] border border-blue-500/20 rounded-xl p-3 text-center" data-id="element-4442" data-mp="qlt8jfjsdg5i">
                    <span className="text-blue-500/60 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-4443" data-mp="vjbjfbhd65g5">
                      Contactados
                    </span>
                    <span className="text-xl font-black text-blue-500" data-id="element-4444" data-mp="2xc994yfughwv">
                      {stats.contacted}
                    </span>
                  </div>
                  <div className="bg-[#141414] border border-emerald-500/20 rounded-xl p-3 text-center" data-id="element-4445" data-mp="3dzxb5vzizawd">
                    <span className="text-emerald-500/60 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-4446" data-mp="3iz080t5cqat9">
                      Aprobados
                    </span>
                    <span className="text-xl font-black text-emerald-500" data-id="element-4447" data-mp="2qaw000zb5lne">
                      {stats.approved}
                    </span>
                  </div>
                  <div className="bg-[#141414] border border-red-500/20 rounded-xl p-3 text-center" data-id="element-4448" data-mp="2va8jqcuvuclr">
                    <span className="text-red-500/60 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-4449" data-mp="307qwkec0tbz1">
                      Rechazados
                    </span>
                    <span className="text-xl font-black text-red-500" data-id="element-4450" data-mp="27jl9hkxhvaw1">
                      {stats.rejected}
                    </span>
                  </div>
                </div>

                {/* Filter Tabs */}
                <div className="flex overflow-x-auto hide-scrollbar gap-2 mt-4 pb-1" data-id="element-4451" data-mp="tntfee1fio7s">
                  {[{
                id: 'all',
                label: 'Todos'
              }, {
                id: 'pending',
                label: 'Pendientes'
              }, {
                id: 'contacted',
                label: 'Contactados'
              }, {
                id: 'approved',
                label: 'Aprobados'
              }, {
                id: 'rejected',
                label: 'Rechazados'
              }].map(tab => <button key={tab.id} onClick={() => setStatusFilter(tab.id as any)} className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${statusFilter === tab.id ? 'bg-white text-[#0B0B0B]' : 'bg-[#141414] text-white/60 border border-white/10 hover:bg-white/5 hover:text-white'}`} data-id="element-4452" data-mp="3o4n6vk4c5is5">
                      {tab.label}
                    </button>)}
                </div>
              </div>

              {/* Panel Content — Scrollable */}
              <div className="flex-1 overflow-y-auto px-6 py-5" data-id="element-4453" data-mp="3s8ftv54bp4yo">
                {appsLoading ? <div className="space-y-4" data-id="element-4454" data-mp="12ehmt8mg5bg">
                    {[1, 2, 3].map(i => <div key={i} className="h-24 bg-[#141414] rounded-2xl border border-white/5 animate-pulse" data-id="element-4455" data-mp="3jg465r0lfvme" />)}
                  </div> : appsError ? <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-8 text-center" data-id="element-4456" data-mp="ezjk6j94mm1y">
                    <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" data-id="element-4457" data-mp="juh44l7y2ftm" />
                    <h3 className="text-lg font-bold text-white mb-1" data-id="element-4458" data-mp="24h6x7gjwujfq">
                      Error al cargar aplicaciones
                    </h3>
                    <p className="text-white/60 text-sm mb-4" data-id="element-4459" data-mp="2e8bewo0z0ie9">{appsError}</p>
                    <button onClick={fetchApplications} className="px-6 py-2 bg-red-500/20 text-red-400 rounded-xl font-bold text-sm hover:bg-red-500/30 transition-colors" data-id="element-4460" data-mp="2eaffox3kp1ab">
                      Reintentar
                    </button>
                  </div> : filteredApps.length === 0 ? <div className="bg-[#141414] border border-white/10 rounded-2xl p-12 text-center" data-id="element-4461" data-mp="1vjo8a135bkyk">
                    <Inbox className="w-12 h-12 text-white/20 mx-auto mb-4" data-id="element-4462" data-mp="1qhp62uj56u37" />
                    <h3 className="text-xl font-bold text-white mb-2" data-id="element-4463" data-mp="36vxi3v6adl0l">
                      Aún no hay aplicaciones
                    </h3>
                    <p className="text-white/50 text-sm" data-id="element-4464" data-mp="320i4n4c6xr4p">
                      Cuando un restaurante aplique a NOIZA, aparecerá aquí.
                    </p>
                  </div> : <div className="space-y-4" data-id="element-4465" data-mp="1ghzjx6r1aud1">
                    {filteredApps.map(app => <div key={app.id} className="bg-[#141414] border border-white/10 rounded-2xl p-5 hover:border-white/20 transition-colors relative overflow-hidden" data-id="element-4466" data-mp="1kk2wxztcq70f">
                        {updatingId === app.id && <div className="absolute inset-0 bg-[#141414]/80 backdrop-blur-sm z-10 flex items-center justify-center" data-id="element-4467" data-mp="1zco3u5jja3zp">
                            <Loader2 className="w-6 h-6 text-white animate-spin" data-id="element-4468" data-mp="287uebhtr1a8k" />
                          </div>}

                        <div className="flex flex-col gap-4" data-id="element-4469" data-mp="2d88lkxnj0g5j">
                          {/* Row 1: Name + Status + Date */}
                          <div className="flex items-start justify-between gap-3" data-id="element-4470" data-mp="2i35pjq5y7zbk">
                            <div data-id="element-4471" data-mp="2s7cemtqk758m">
                              <div className="flex items-center gap-2 mb-1" data-id="element-4472" data-mp="2nalyy7io2bnq">
                                <h3 className="font-bold text-lg text-white" data-id="element-4473" data-mp="2ncozhlhg3n6p">
                                  {app.restaurant_name}
                                </h3>
                                {getStatusBadge(app.status)}
                              </div>
                              <p className="text-sm text-white/60" data-id="element-4474" data-mp="3azf1imtsnhrb">
                                {app.owner_name} • {app.business_type}
                              </p>
                            </div>
                            <p className="text-xs text-white/40 shrink-0" data-id="element-4475" data-mp="318alrkwansvf">
                              {new Date(app.created_at).toLocaleDateString('es-CR', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                            </p>
                          </div>

                          {/* Row 2: Quick Info */}
                          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm" data-id="element-4476" data-mp="1dd4ey07a7q4q">
                            <div className="flex items-center gap-2" data-id="element-4477" data-mp="1ib5tq8xod3ae">
                              <span className="text-white/40" data-id="element-4478" data-mp="1i92wp4y3u9w6">WhatsApp:</span>
                              <span className="text-white font-medium" data-id="element-4479" data-mp="14a3bk79swd4r">
                                {app.whatsapp}
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-4480" data-mp="ues0q01ijq2a">
                              <span className="text-white/40" data-id="element-4481" data-mp="zbrylno7lmib">
                                Menú digital:
                              </span>
                              <span className="text-white font-medium" data-id="element-4482" data-mp="2osazteb6zgpp">
                                {app.has_digital_menu ? 'Sí' : 'No'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-4483" data-mp="i0wh46ynw4uq">
                              <span className="text-white/40" data-id="element-4484" data-mp="d5qwdbgzww88">Pedidos WA:</span>
                              <span className="text-white font-medium" data-id="element-4485" data-mp="ru3uof3k2r5w">
                                {app.uses_whatsapp_orders ? 'Sí' : 'No'}
                              </span>
                            </div>
                          </div>

                          {/* Row 3: Actions */}
                          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-white/5" data-id="element-4486" data-mp="wr3ptuyva9i8">
                            <button onClick={() => {
                      setSelectedApp(app);
                      setDetailOpen(true);
                    }} className="px-3 py-2 bg-white/5 hover:bg-white/10 text-white text-sm font-bold rounded-xl transition-colors flex items-center gap-1.5" data-id="element-4487" data-mp="1aqv7q6si8a1i">
                              <Eye className="w-4 h-4" data-id="element-4488" data-mp="2lqhnc8lq7i3c" /> Ver detalle
                            </button>
                            <button onClick={() => handleWhatsApp(app.whatsapp)} disabled={!app.whatsapp} className="px-3 py-2 bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#25D366] text-sm font-bold rounded-xl transition-colors disabled:opacity-50 flex items-center gap-1.5" data-id="element-4489" data-mp="2qoav362ogk5q">
                              <MessageCircle className="w-4 h-4" data-id="element-4490" data-mp="27rrpgqr31edx" /> WhatsApp
                            </button>

                            <div className="flex-1" data-id="element-4491" data-mp="2csfgvdj83x8o" />

                            {app.status === 'pending' && <button onClick={() => updateStatus(app.id, 'contacted')} className="px-3 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 text-sm font-bold rounded-xl transition-colors" data-id="element-4492" data-mp="2hn2zwwvgjzfv">
                                Contactado
                              </button>}
                            {app.status !== 'approved' && <button onClick={() => updateStatus(app.id, 'approved')} className="px-3 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-sm font-bold rounded-xl transition-colors" data-id="element-4493" data-mp="12rbg21nelp4x">
                                Aprobar
                              </button>}
                            {app.status !== 'rejected' && <button onClick={() => updateStatus(app.id, 'rejected')} className="px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm font-bold rounded-xl transition-colors" data-id="element-4494" data-mp="2j74iqgtqreuw">
                                Rechazar
                              </button>}
                          </div>
                        </div>
                      </div>)}
                  </div>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* ═══ SUSPENSIÓN / REACTIVACIÓN ═══ */}
      {suspensionTarget && <Modal isOpen={true} onClose={() => {
      if (!suspensionSaving) setSuspensionTarget(null);
    }} title={suspensionTarget.suspend ? 'Suspender restaurante' : 'Reactivar restaurante'} data-id="element-4495" data-mp="2gl19tvs128gm">
          <div className="space-y-6" data-id="element-4496" data-mp="9rphj6apte99">
            {suspensionTarget.suspend ? <>
                <p className="text-white/70" data-id="element-4497" data-mp="5qvpc0q8pdak">
                  Estás a punto de suspender temporalmente:
                </p>
                <p className="text-xl font-bold text-white" data-id="element-4498" data-mp="dd0vheyuxumh">
                  {suspensionTarget.name}
                </p>
                <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4" data-id="element-4499" data-mp="3d09tmdin2f4">
                  <p className="text-white/70 text-sm mb-2" data-id="element-4500" data-mp="8bv4pmorchfl">
                    Esta acción bloqueará:
                  </p>
                  <ul className="text-white/60 text-sm space-y-1 list-disc pl-5" data-id="element-4501" data-mp="w0w8gwmnb4ej">
                    <li data-id="element-4502" data-mp="11094a6vhvlt9">acceso administrativo</li>
                    <li data-id="element-4503" data-mp="10zzd4fypr8lz">caja y operación</li>
                    <li data-id="element-4504" data-mp="mbw4eytadith">página pública</li>
                    <li data-id="element-4505" data-mp="mauu4wk3h0ct">recepción de nuevos pedidos</li>
                  </ul>
                </div>
                <p className="text-white/50 text-sm" data-id="element-4506" data-mp="r9eeisj1i432">
                  Los datos existentes NO serán eliminados.
                </p>
                <p className="text-white/50 text-sm" data-id="element-4507" data-mp="1ey76yf0gdtsx">
                  Motivo:{' '}
                  <span className="text-white/80 font-medium" data-id="element-4508" data-mp="1jsbcctu2ltrw">
                    Pago pendiente
                  </span>
                </p>
              </> : <p className="text-white/70" data-id="element-4509" data-mp="1op7asolt3a8a">
                ¿Deseás restablecer completamente el servicio de{' '}
                <span className="text-white font-bold" data-id="element-4510" data-mp="1kkr80w7hoixa">
                  {suspensionTarget.name}
                </span>
                ?
              </p>}

            <div className="flex gap-3 pt-2" data-id="element-4511" data-mp="1ap69x95jk2st">
              <button onClick={() => setSuspensionTarget(null)} disabled={suspensionSaving} className="flex-1 px-4 py-3 rounded-xl border border-white/15 text-white/70 font-semibold hover:bg-white/5 transition-colors disabled:opacity-50" data-id="element-4512" data-mp="1aq7in6z34d7d">
                Cancelar
              </button>
              <button onClick={applySuspension} disabled={suspensionSaving} className={`flex-1 px-4 py-3 rounded-xl font-semibold text-white transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2 ${suspensionTarget.suspend ? 'bg-red-600 hover:bg-red-500' : 'bg-emerald-600 hover:bg-emerald-500'}`} data-id="element-4513" data-mp="rxwqo85yxzo1">
                {suspensionSaving && <Loader2 className="w-4 h-4 animate-spin" data-id="element-4514" data-mp="38kuvja79gvuk" />}
                {suspensionTarget.suspend ? 'Confirmar suspensión' : 'Confirmar reactivación'}
              </button>
            </div>
          </div>
        </Modal>}

      {/* ═══ DETAIL MODAL ═══ */}
      {selectedApp && <Modal isOpen={detailOpen} onClose={() => setDetailOpen(false)} title="Detalle de Aplicación" data-id="element-4515" data-mp="2ovki62uzmmzm">
          <div className="space-y-6" data-id="element-4516" data-mp="2todlcmat5n9l">
            <div className="flex items-center justify-between border-b border-white/10 pb-4" data-id="element-4517" data-mp="1x4wh0qtbdhly">
              <div data-id="element-4518" data-mp="216i392rqn88t">
                <h3 className="text-2xl font-bold text-white" data-id="element-4519" data-mp="215zvp9zml7ll">
                  {selectedApp.restaurant_name}
                </h3>
                <p className="text-white/60" data-id="element-4520" data-mp="1i2odjfm830jr">{selectedApp.owner_name}</p>
              </div>
              {getStatusBadge(selectedApp.status)}
            </div>

            <div className="grid grid-cols-2 gap-6" data-id="element-4521" data-mp="1rrg2rfzwf6qz">
              <div data-id="element-4522" data-mp="1rt0vr16kehmq">
                <p className="text-white/40 text-sm mb-1" data-id="element-4523" data-mp="1rtj1qpj14a7u">WhatsApp</p>
                <p className="text-white font-medium" data-id="element-4524" data-mp="1mzeu041xvy5p">{selectedApp.whatsapp}</p>
              </div>
              <div data-id="element-4525" data-mp="20qtb25cadpa4">
                <p className="text-white/40 text-sm mb-1" data-id="element-4526" data-mp="20uh28xu9dzlo">Instagram/Facebook</p>
                <p className="text-white font-medium" data-id="element-4527" data-mp="1vt1jxdjgu76z">
                  {selectedApp.social_handle || '-'}
                </p>
              </div>
              <div data-id="element-4528" data-mp="25rquel918pd7">
                <p className="text-white/40 text-sm mb-1" data-id="element-4529" data-mp="25sio1qj2kd0u">Tipo de negocio</p>
                <p className="text-white font-medium" data-id="element-4530" data-mp="gc9k88jgns96">
                  {selectedApp.business_type}
                </p>
              </div>
              <div data-id="element-4531" data-mp="bereer0qpt5e">
                <p className="text-white/40 text-sm mb-1" data-id="element-4532" data-mp="bfj2lgs0coln">Fecha de solicitud</p>
                <p className="text-white font-medium" data-id="element-4533" data-mp="2n1bi8uyumfne">
                  {new Date(selectedApp.created_at).toLocaleString('es-CR')}
                </p>
              </div>
              <div data-id="element-4534" data-mp="2dbrw9gfua5ob">
                <p className="text-white/40 text-sm mb-1" data-id="element-4535" data-mp="2da73nwd34wt3">
                  ¿Usa pedidos por WhatsApp?
                </p>
                <p className="text-white font-medium" data-id="element-4536" data-mp="2i8074hn07cpo">
                  {selectedApp.uses_whatsapp_orders ? 'Sí' : 'No'}
                </p>
              </div>
              <div data-id="element-4537" data-mp="28cyhlkotg8mk">
                <p className="text-white/40 text-sm mb-1" data-id="element-4538" data-mp="28dgm14lmtt5k">
                  ¿Tiene menú digital?
                </p>
                <p className="text-white font-medium" data-id="element-4539" data-mp="1ue8n8ctdsn1w">
                  {selectedApp.has_digital_menu ? 'Sí' : 'No'}
                </p>
              </div>
            </div>

            <div data-id="element-4540" data-mp="3oqooez7blazw">
              <p className="text-white/40 text-sm mb-1" data-id="element-4541" data-mp="3oo2hn1s96ziq">Objetivo principal</p>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-id="element-4542" data-mp="3tnx5sxkn9b03">
                <p className="text-white/90" data-id="element-4543" data-mp="3s949hxyh1svj">
                  {selectedApp.main_goal || 'No especificado'}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 pt-4 border-t border-white/10" data-id="element-4544" data-mp="3i9wu7586fo5s">
              <button onClick={() => handleWhatsApp(selectedApp.whatsapp)} className="flex-1 py-3 bg-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/30 rounded-xl font-bold flex items-center justify-center gap-2 transition-colors" data-id="element-4545" data-mp="3ibq5ixtqiwk4">
                <MessageCircle className="w-5 h-5" data-id="element-4546" data-mp="9uvcsb6nisjr" /> WhatsApp
              </button>

              {selectedApp.status === 'pending' && <button onClick={() => updateStatus(selectedApp.id, 'contacted')} className="flex-1 py-3 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-xl font-bold transition-colors" data-id="element-4547" data-mp="4jvzo1vcovg">
                  Marcar Contactado
                </button>}
              {selectedApp.status !== 'approved' && <button onClick={() => updateStatus(selectedApp.id, 'approved')} className="flex-1 py-3 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 rounded-xl font-bold transition-colors" data-id="element-4548" data-mp="113yag8inyo8a">
                  Aprobar
                </button>}
              {selectedApp.status !== 'rejected' && <button onClick={() => updateStatus(selectedApp.id, 'rejected')} className="flex-1 py-3 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-xl font-bold transition-colors" data-id="element-4549" data-mp="1k07kmkqg2un0">
                  Rechazar
                </button>}
            </div>
          </div>
        </Modal>}

      {/* Toast Notification */}
      {toast && <div className="fixed bottom-6 right-6 z-[60] animate-in slide-in-from-bottom-5 fade-in duration-300" data-id="element-4550" data-mp="22p4fappr7p5r">
          <div className={`flex items-center gap-3 px-6 py-4 rounded-2xl shadow-2xl border ${toast.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'} backdrop-blur-md`} data-id="element-4551" data-mp="2t0z3l3wc09t0">
            {toast.type === 'success' ? <CheckCircle2 className="w-5 h-5" data-id="element-4552" data-mp="2x22j1pagw3tp" /> : <AlertCircle className="w-5 h-5" data-id="element-4553" data-mp="2x0rf9ph0uw2l" />}
            <span className="font-bold text-sm" data-id="element-4554" data-mp="29dswum9cde21">{toast.message}</span>
          </div>
        </div>}
    </div>;
}